<template>
  <a-drawer
    :visible="visible"
    :title="null"
    placement="right"
    width="580"
    :destroy-on-close="true"
    :header-style="{ display: 'none' }"
    :body-style="{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }"
    @close="$emit('close')"
  >
    <div class="order-history-hero">
      <div class="hero-top">
        <div class="hero-title-group">
          <div>
            <div class="hero-title">
              {{ historyTitle }}
            </div>
            <div class="hero-subtitle">
              <span v-if="!loading && historyLogs.length" class="text-muted fs-12px">
                {{ historyLogs.length }} {{ $t('order.history_activities') || 'Aktivitas Tercatat' }}
              </span>
              <span v-else-if="loading" class="text-muted fs-12px">
                {{ $t('utils.loading') || 'Memuat data...' }}
              </span>
              <span v-else class="text-muted fs-12px">
                {{ $t('order.no_activity') || 'Tidak ada aktivitas' }}
              </span>
            </div>
          </div>
        </div>

        <div class="hero-actions">
          <a-tooltip :title="$t('utils.refresh') || 'Segarkan'">
            <span class="d-inline-flex">
              <button
                class="hero-action-btn"
                :disabled="loading"
                @click="fetchHistory"
              >
                <a-icon :type="loading ? 'loading' : 'reload'" />
              </button>
            </span>
          </a-tooltip>
          <a-tooltip :title="$t('utils.close') || 'Tutup'">
            <span class="d-inline-flex">
              <button class="hero-action-btn" @click="$emit('close')">
                <a-icon type="close" />
              </button>
            </span>
          </a-tooltip>
        </div>
      </div>

      <div
        v-if="orderData"
        class="order-context-card"
        :class="{ 'is-product-history': isProductHistory, 'is-order-history': !isProductHistory }"
      >
        <div class="order-context-row">
          <div class="order-context-meta">
            <div class="order-context-label">
              {{ primaryContextLabel }}
            </div>
            <div class="order-context-number" @click="copyText(primaryContextValue, primaryContextLabel)">
              <span v-if="isProductHistory && productSkus.length > 1" class="product-sku-list">
                <a-tag
                  v-for="sku in productSkus"
                  :key="sku"
                  class="product-sku-chip"
                  @click.stop="copyText(sku, $t('common.sku') || 'SKU')"
                >
                  {{ sku }}
                  <a-icon type="copy" class="product-sku-copy-icon" />
                </a-tag>
              </span>
              <span v-else>{{ primaryContextValue || '-' }}</span>
              <a-tooltip v-if="!(isProductHistory && productSkus.length > 1)" :title="$t('utils.copy') || 'Salin'">
                <a-icon type="copy" class="copy-icon" />
              </a-tooltip>
            </div>
          </div>

          <div v-if="!isProductHistory && wordingSalesState" class="order-context-status">
            <a-tooltip placement="top">
              <template slot="title">
                <span>{{ $t(`order.filterState.${wordingSalesState}`) }}</span>
              </template>
              <a-button
                shape="round"
                size="small"
                class="text-white px-2 mr-0 fs-10px border-0"
                :style="{ background: getBackgroundColorStatus, height: '22px', lineHeight: '22px', fontSize: '10px' }"
              >
                {{ $t(`order.filterStateTag.${wordingSalesState}`) }}
              </a-button>
            </a-tooltip>
          </div>
        </div>

        <div class="order-context-details">
          <div class="context-detail-row">
            <span class="context-detail-label">{{ isProductHistory ? ($t('product.product_name') || 'Produk') : ($t('order.business') || 'Bisnis') }}</span>
            <span class="context-detail-value">{{ isProductHistory ? (productName || '-') : (storeOrBusinessName || '-') }}</span>
          </div>
          <div class="context-detail-row">
            <span class="context-detail-label">{{ isProductHistory ? ($t('store_list_page.product_status') || 'Status') : ($t('order.channel') || 'Channel') }}</span>
            <span class="context-detail-value">{{ isProductHistory ? (productStatus || '-') : (channelName || '-') }}</span>
          </div>
          <div v-if="!isProductHistory" class="context-detail-row">
            <span class="context-detail-label">{{ isProductHistory ? ($t('order.channel') || 'Channel') : ($t('order.customer') || 'Pelanggan') }}</span>
            <span class="context-detail-value">{{ isProductHistory ? (channelName || '-') : (customerName || '-') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading && historyLogs.length > 1" class="drawer-toolbar">
      <div class="d-flex align-items-center justify-content-end w-100">
        <a-button
          size="small"
          type="link"
          class="sort-toggle-btn"
          @click="isDescending = !isDescending"
        >
          <a-icon :type="isDescending ? 'sort-descending' : 'sort-ascending'" />
          <span>{{ isDescending ? ($t('order.sort_newest') || 'Terbaru Dulu') : ($t('order.sort_oldest') || 'Terlama Dulu') }}</span>
        </a-button>
      </div>
    </div>

    <div class="drawer-content-area">
      <div v-if="loading" class="p-4">
        <div v-for="n in 4" :key="n" class="skeleton-log-item mb-4">
          <a-skeleton active :paragraph="{ rows: 2, width: ['90%', '50%'] }" :title="{ width: '35%' }" />
        </div>
      </div>

      <div v-else-if="hasError" class="p-4 text-center my-auto">
        <div class="error-container">
          <a-icon type="warning" class="error-icon" />
          <h5 class="error-title">
            {{ historyFailedToLoad }}
          </h5>
          <p class="error-desc text-muted">
            {{ errorMessage || ($t('order.history_fetch_error_desc') || 'Terjadi kesalahan saat mengambil log dari server.') }}
          </p>
          <a-button type="primary" icon="reload" @click="fetchHistory">
            {{ $t('utils.try_again') || 'Coba Lagi' }}
          </a-button>
        </div>
      </div>

      <div v-else-if="sortedLogs.length === 0" class="p-4 text-center my-auto">
        <a-empty :description="historyEmptyMessage">
          <template #image>
            <a-icon type="file-search" style="font-size: 44px; color: #bfbfbf;" />
          </template>
        </a-empty>
      </div>

      <div v-else class="simplified-timeline-wrapper">
        <a-timeline class="simplified-timeline">
          <a-timeline-item
            v-for="(log, index) in sortedLogs"
            :key="`${log.code}-${log.time}-${index}`"
            :class="[getStepThemeClass(log.code), { 'has-divider': index > 0 }]"
          >
            <template #dot>
              <div class="timeline-custom-dot" :class="getLogThemeClass(log.code)" />
            </template>

            <div class="log-step">
              <a-divider v-if="index > 0" class="log-step-divider" />
              <div class="log-step-header">
                <a-tag :color="getTagColor(log.code)" class="log-tag">
                  {{ (log.code || '---').replace(/_/g, ' ') }}
                </a-tag>
                <span class="log-time" :title="getExactLocalTime(log.time)">
                  {{ formatLocalDate(log.time) }}
                </span>
              </div>

              <div class="log-step-desc">
                {{ log.description || '-' }}
              </div>

              <div class="log-step-footer">
                <div class="log-step-actor">
                  <template v-if="isSystemActor(log.user)">
                    <span class="actor-tag actor-system">
                      <a-icon type="setting" theme="filled" class="mr-1" />
                      System
                    </span>
                  </template>
                  <template v-else>
                    <span class="actor-tag actor-user" @click="copyText(log.user, 'Email Pengguna')">
                      <a-icon type="user" class="mr-1" />
                      <span>{{ log.user || '-' }}</span>
                      <a-icon type="copy" class="ml-1 copy-actor-btn" />
                    </span>
                  </template>
                </div>

                <a-button
                  v-if="!isProductHistory && canShowEventDetail(log)"
                  type="link"
                  size="small"
                  class="log-detail-trigger"
                  :loading="detailLoadingEventId === eventDetailKey(log)"
                  @click="toggleEventDetail(log)"
                >
                  {{ eventDetails[eventDetailKey(log)] !== undefined ? $t('order.closeDetail') : $t('order.viewDetail') }}
                </a-button>
              </div>

              <template v-if="!isProductHistory && canShowEventDetail(log)">
                <a-skeleton
                  v-if="detailLoadingEventId === eventDetailKey(log)"
                  active
                  class="log-detail-skeleton"
                  :title="false"
                  :paragraph="{ rows: 2, width: ['92%', '68%'] }"
                />
                <div v-else-if="eventDetails[eventDetailKey(log)] !== undefined" class="log-detail-content">
                  {{ eventDetails[eventDetailKey(log)] }}
                </div>
              </template>
            </div>
          </a-timeline-item>
        </a-timeline>
      </div>
    </div>

    <div class="drawer-footer">
      <div class="footer-timezone-info">
        <a-icon type="clock-circle" class="mr-1" />
        <span>Timezone: {{ clientTimezone }}</span>
      </div>
      <a-button type="default" @click="$emit('close')">
        {{ $t('utils.close') || 'Tutup' }}
      </a-button>
    </div>
  </a-drawer>
</template>

<script>
import moment from 'moment'
import { getHistoryEventDetail, getOrderHistoryLog, getProductHistoryLog } from '@/api/sales'

export default {
  name: 'DrawerOrderHistory',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    orderData: {
      type: Object,
      default: () => ({}),
    },
    businessId: {
      type: [String, Number],
      default: '',
    },
    workspaceId: {
      type: [String, Number],
      default: '',
    },
    historyType: {
      type: String,
      default: 'order',
    },
  },
  data() {
    return {
      loading: false,
      historyLogs: [],
      hasError: false,
      errorMessage: '',
      isDescending: true,
      abortController: null,
      detailAbortController: null,
      detailLoadingEventId: null,
      eventDetails: {},
    }
  },
  computed: {
    isProductHistory() {
      return this.historyType === 'product'
    },
    historyTitle() {
      return this.isProductHistory
        ? (this.$t('store_list_page.synchronize.product_history_title') || 'Riwayat Produk')
        : (this.$t('order.history_order_title') || 'Riwayat Log Pesanan')
    },
    historyEmptyMessage() {
      return this.isProductHistory
        ? (this.$t('store_list_page.synchronize.product_history_empty') || 'Belum ada riwayat untuk produk ini.')
        : (this.$t('order.history_empty_msg') || 'Belum ada data riwayat log untuk pesanan ini.')
    },
    historyFailedToLoad() {
      return this.isProductHistory
        ? (this.$t('store_list_page.synchronize.product_history_failed') || 'Gagal Memuat Riwayat Produk')
        : (this.$t('order.history_failed_to_load') || 'Gagal Memuat Riwayat Order')
    },
    primaryContextLabel() {
      if (!this.isProductHistory) return this.$t('order.orderNumber') || 'No. Order'
      return this.productSku
        ? (this.$t('common.sku') || 'SKU')
        : (this.$t('store_list_page.synchronize.product_id_label') || 'ID Produk')
    },
    primaryContextValue() {
      if (!this.isProductHistory) return this.orderData?.order_number || ''
      return this.productSku || this.orderData?.product_id || this.orderData?.channel_product_id || this.orderData?.id || ''
    },
    productSkus() {
      const skus = [
        this.orderData?.sku,
        ...(this.orderData?.catalogs || []).map(catalog => catalog?.sku),
      ].filter(Boolean)
      return [...new Set(skus)]
    },
    productSku() {
      return this.productSkus.join(', ')
    },
    productName() {
      return this.orderData?.title || this.orderData?.name || this.orderData?.detail?.title || ''
    },
    productStatus() {
      return this.orderData?.product_status || this.orderData?.status || ''
    },
    clientTimezone() {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        const offset = moment().format('Z')
        return `${tz} (UTC${offset})`
      } catch (e) {
        return moment().format('Z')
      }
    },
    channelName() {
      return (
        this.orderData?.channel?.sales_channel?.name ||
        this.orderData?.channel?.sales_channel?.channel_name ||
        this.orderData?.channel?.name ||
        this.orderData?.channel?.channel_title ||
        ''
      )
    },
    storeOrBusinessName() {
      return (
        this.orderData?.business?.business_title ||
        this.orderData?.business?.name ||
        this.orderData?.channel?.name ||
        ''
      )
    },
    customerName() {
      return this.orderData?.customer_name?.trim() || ''
    },
    getBackgroundColorStatus() {
      const sales_state = this.orderData?.sales_state
      const shipping_state = this.orderData?.shipping_state
      const channelCode = this.orderData?.channel?.code || this.orderData?.channel?.sales_channel?.code

      if (!sales_state) {
        return null
      }

      switch (sales_state.toLowerCase()) {
        case 'submitted': 
        case 'created': 
          return '#0A89FF' 
        case 'completed':
          return '#454655' 
        case 'accepted':
          if (channelCode?.toLowerCase() === 'distributor' && shipping_state?.toLowerCase() === 'buyer_accepted') {
            return '#2D9500'
          }
          return shipping_state?.toLowerCase() === 'shipped' || shipping_state?.toLowerCase() === 'delivered'
            ? '#2D9500'
            : shipping_state?.toLowerCase() === 'buyer_accepted'
            ? '#454655'
            : '#00BBD9'
        case 'verified':
          return '#8A8EBA' 
        case 'delivered': 
          return '#2D9500'
        case 'canceled':
        case 'cancelled':
          return '#E00000'
        default:
          return '#EC4426'
      }
    },
    wordingSalesState() {
      const sales_state = this.orderData?.sales_state
      const shipping_state = this.orderData?.shipping_state
      const channelCode = this.orderData?.channel?.code || this.orderData?.channel?.sales_channel?.code

      if (!sales_state) {
        return null
      }

      switch (sales_state.toLowerCase()) {
        case 'verified': 
          return 'verified'
        case 'submitted': 
        case 'created': 
          return 'new'
        case 'accepted': 
          if (channelCode?.toLowerCase() === 'distributor' && shipping_state?.toLowerCase() === 'buyer_accepted') {
            return 'delivered'
          }
          return shipping_state?.toLowerCase() === 'shipped'
            ? 'shipped'
            : shipping_state?.toLowerCase() === 'delivered'
            ? 'delivered'
            : shipping_state?.toLowerCase() === 'buyer_accepted'
            ? 'completed'
            : 'onprocess'
        case 'shipped': 
          return 'shipped'
        case 'delivered': 
          return 'send'
        case 'completed': 
          return 'completed'
        case 'canceled':
        case 'cancelled':
        case 'rejected': 
        case 'canceledpaymentexpired':
          return 'canceled'
        case 'cancelrequested':
        case 'cancel_requested':
          return 'cancelrequested'
        default:
          return null
      }
    },
    sortedLogs() {
      const list = [...this.historyLogs]
      list.sort((a, b) => {
        const timeA = moment(a.time).valueOf()
        const timeB = moment(b.time).valueOf()
        return this.isDescending ? timeB - timeA : timeA - timeB
      })
      return list
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.isDescending = true
        this.fetchHistory()
      } else {
        if (this.abortController) {
          this.abortController.abort()
          this.abortController = null
        }
        if (this.detailAbortController) {
          this.detailAbortController.abort()
          this.detailAbortController = null
        }
      }
    },
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    if (this.detailAbortController) {
      this.detailAbortController.abort()
      this.detailAbortController = null
    }
  },
  methods: {
    async fetchHistory() {
      const powerbizId = this.isProductHistory
        ? (this.orderData?.channel_product_id || this.orderData?.product_id || this.orderData?.id)
        : this.orderData?.id
      const resolvedBusinessId =
        this.orderData?.business_id ||
        this.orderData?.business?.id ||
        this.businessId ||
        this.$route.query?.business_id

      if (!powerbizId) {
        this.historyLogs = []
        return
      }

      if (this.abortController) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()

      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      if (this.detailAbortController) {
        this.detailAbortController.abort()
        this.detailAbortController = null
      }
      this.detailLoadingEventId = null
      this.eventDetails = {}

      const lang = (this.$i18n?.locale || 'id').split('-')[0]

      try {
        const getHistory = this.isProductHistory ? getProductHistoryLog : getOrderHistoryLog
        const response = await getHistory({
          powerbizId,
          businessId: resolvedBusinessId,
          workspaceId: this.workspaceId || this.$route.query?.workspace_id,
          lang,
          signal: this.abortController.signal,
        })

        const rawData = response?.data?.data || []
        this.historyLogs = Array.isArray(rawData) ? rawData : []
      } catch (err) {
        if (err.name === 'AbortError' || err.message === 'canceled') {
          return
        }
        console.error('Failed to fetch history log:', err)
        this.hasError = true
        this.errorMessage = err?.response?.data?.message || err?.message || 'Failed to load history log.'
        this.historyLogs = []
      } finally {
        this.loading = false
      }
    },

    async toggleEventDetail(log) {
      const eventId = log?.event_id
      const detailKey = this.eventDetailKey(log)
      if (!detailKey || this.detailLoadingEventId) return

      if (this.eventDetails[detailKey] !== undefined) {
        this.$delete(this.eventDetails, detailKey)
        return
      }

      if (!eventId) return

      if (this.detailAbortController) this.detailAbortController.abort()
      this.detailAbortController = new AbortController()
      this.detailLoadingEventId = detailKey

      try {
        const response = await getHistoryEventDetail({
          eventId,
          lang: (this.$i18n?.locale || 'id').split('-')[0],
          signal: this.detailAbortController.signal,
        })
        this.$set(this.eventDetails, detailKey, response?.data?.description || '-')
      } catch (err) {
        if (err.name !== 'AbortError' && err.message !== 'canceled') {
          this.$notification.error({
            message: err?.response?.data?.message || err?.message || 'Failed to load event detail.',
          })
        }
      } finally {
        this.detailLoadingEventId = null
      }
    },

    canShowEventDetail(log) {
      return Boolean(log?.event_id && log.is_detail_available)
    },

    eventDetailKey(log) {
      return log?.event_id
    },

    formatLocalDate(timeStr) {
      if (!timeStr) return '-'
      return moment(timeStr).local().format('DD MMM YYYY, HH:mm:ss')
    },

    getExactLocalTime(timeStr) {
      if (!timeStr) return ''
      return moment(timeStr).local().format('dddd, DD MMMM YYYY, HH:mm:ss [UTC]Z')
    },

    isSystemActor(user) {
      if (!user) return true
      const u = user.toLowerCase().trim()
      return u === 'system' || u === 'system automation' || u === 'auto'
    },

    getTagColor(code) {
      const c = (code || '').toUpperCase()
      if (['PAID', 'COMPLETED', 'ACCEPTED', 'DELIVERED', 'SUCCESS'].includes(c)) return 'green'
      if (['CREATED', 'SUBMITTED', 'NEW'].includes(c)) return 'blue'
      if (['ON_PROCESS', 'ONPROCESS', 'PROCESSING'].includes(c)) return 'orange'
      if (['CANCELED', 'CANCELLED', 'REJECTED', 'FAILED'].includes(c)) return 'red'
      return 'gray'
    },

    getStepThemeClass(code) {
      const c = (code || '').toUpperCase()
      if (['PAID', 'COMPLETED', 'ACCEPTED', 'DELIVERED', 'SUCCESS'].includes(c)) return 'step-green'
      if (['CREATED', 'SUBMITTED', 'NEW'].includes(c)) return 'step-blue'
      if (['ON_PROCESS', 'ONPROCESS', 'PROCESSING', 'IN_PROGRESS'].includes(c)) return 'step-orange'
      if (['CANCELED', 'CANCELLED', 'REJECTED', 'FAILED', 'ERROR'].includes(c)) return 'step-red'
      return 'step-gray'
    },

    getLogThemeClass(code) {
      const c = (code || '').toUpperCase()
      if (['PAID', 'COMPLETED', 'ACCEPTED', 'DELIVERED', 'SUCCESS'].includes(c)) return 'dot-green'
      if (['CREATED', 'SUBMITTED', 'NEW'].includes(c)) return 'dot-blue'
      if (['ON_PROCESS', 'ONPROCESS', 'PROCESSING'].includes(c)) return 'dot-orange'
      if (['SHIPPED', 'IN_TRANSIT'].includes(c)) return 'dot-purple'
      if (['CANCELED', 'CANCELLED', 'REJECTED', 'FAILED'].includes(c)) return 'dot-red'
      return 'dot-gray'
    },

    copyText(text, label) {
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$notification.success({
            message: `${label || 'Teks'} berhasil disalin`,
            description: text,
            duration: 2.5,
          })
        }).catch(() => {
          this.fallbackCopyText(text, label)
        })
      } else {
        this.fallbackCopyText(text, label)
      }
    },

    fallbackCopyText(text, label) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      try {
        document.execCommand('copy')
        this.$notification.success({
          message: `${label || 'Teks'} berhasil disalin`,
          description: text,
          duration: 2.5,
        })
      } catch (err) {
        console.error('Failed to copy', err)
      }
      document.body.removeChild(textarea)
    },
  },
}
</script>

<style lang="scss" scoped>
.order-history-hero {
  background: #f8fafc;
  padding: 16px 20px 14px;
  border-bottom: 1px solid #eef2f6;

  .hero-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .hero-title-group {
    display: flex;
    align-items: center;
  }

  .hero-title {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1.2;
  }

  .hero-subtitle {
    margin-top: 2px;
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .hero-action-btn,
  .hero-btn,
  .hero-close-btn {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 13px;
    padding: 0;
    transition: all 0.2s ease;

    &:hover {
      background: #f1f5f9;
      color: #0052cc;
      border-color: #cbd5e1;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
}

.order-context-card {
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 10px 12px;

  .order-context-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .order-context-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
  }

  .order-context-number {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;

    &:hover {
      color: #0052cc;
      .copy-icon {
        color: #0052cc;
      }
    }

    .copy-icon {
      font-size: 12px;
      color: #94a3b8;
      transition: color 0.2s;
    }

    .product-sku-list {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .product-sku-chip {
      margin: 0;
      color: #1e293b;
      background: #f1f5f9;
      border-color: #e2e8f0;
      cursor: pointer;
    }

    .product-sku-copy-icon {
      margin-left: 4px;
      font-size: 10px;
    }
  }

  .order-context-details {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding-top: 8px;
    border-top: 1px dashed #e2e8f0;
  }

  .context-detail-row {
    display: grid;
    grid-template-columns: 76px 1fr;
    align-items: baseline;
    gap: 6px;
    font-size: 12px;
  }

  .context-detail-label {
    color: #94a3b8;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
  }

  .context-detail-value {
    color: #1e293b;
    font-size: 12px;
    font-weight: 500;
    word-break: break-word;
  }

  &.is-product-history {
    background: transparent;
    border: 0;
    padding: 0;

    .order-context-row {
      margin-bottom: 8px;
    }

    .order-context-details {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 130px;
      gap: 12px;
      padding-top: 0;
      border-top: 0;
    }

    .context-detail-row {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
  }

  &.is-order-history {
    background: transparent;
    border: 0;
    padding: 0;

    .order-context-row {
      margin-bottom: 8px;
    }

    .order-context-details {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
      padding-top: 0;
      border-top: 0;
    }

    .context-detail-row {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
  }
}

.drawer-toolbar {
  background: #ffffff;
  padding: 8px 18px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;

  .sort-toggle-btn {
    color: #64748b;
    font-size: 12px;
    padding: 0 4px;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover {
      color: #0052cc;
    }
  }
}

.drawer-content-area {
  flex: 1;
  overflow-y: auto;
  background: #ffffff;
  padding: 20px 20px 10px;
}

.simplified-timeline-wrapper {
  padding: 4px 4px;
}

.simplified-timeline {
  ::v-deep .ant-timeline-item {
    padding-bottom: 22px;
    position: relative;

    &:last-child {
      padding-bottom: 8px;
    }

    .ant-timeline-item-tail {
      left: 4px;
      top: 14px;
      height: calc(100% - 14px);
      border-left: 2px solid #e2e8f0;
    }

    .ant-timeline-item-head-custom {
      left: 0;
      top: 4px;
      padding: 0;
      margin: 0;
      transform: none;
    }

    .ant-timeline-item-content {
      margin-left: 22px;
      top: 0;
    }

    &.has-divider {
      .ant-timeline-item-head-custom {
        top: 10px;
      }
      .ant-timeline-item-tail {
        top: 20px;
        height: calc(100% - 20px);
      }
    }

    &.step-green {
      .ant-timeline-item-tail {
        border-left-color: #86efac;
      }
    }
    &.step-blue {
      .ant-timeline-item-tail {
        border-left-color: #93c5fd;
      }
    }
    &.step-orange {
      .ant-timeline-item-tail {
        border-left-color: #fed7aa;
      }
    }
    &.step-purple {
      .ant-timeline-item-tail {
        border-left-color: #d8b4fe;
      }
    }
    &.step-red {
      .ant-timeline-item-tail {
        border-left-color: #fca5a5;
      }
    }
  }
}

.timeline-custom-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-sizing: border-box;

  &.dot-green  { background: #4ade80; }
  &.dot-blue   { background: #60a5fa; }
  &.dot-orange { background: #fb923c; }
  &.dot-purple { background: #c084fc; }
  &.dot-red    { background: #f87171; }
  &.dot-gray   { background: #94a3b8; }
}

.log-step {
  padding-bottom: 2px;

  .log-step-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;
  }

  .log-tag {
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    padding: 0 6px;
    margin: 0;
    border-radius: 4px;
  }

  .log-time {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
  }

  .log-step-desc {
    font-size: 13px;
    color: #1e293b;
    line-height: 1.4;
    margin-bottom: 4px;
  }

  .log-step-actor {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
  }

  .log-step-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .log-detail-trigger {
    height: auto;
    padding: 0;
    font-size: 11px;
  }

  .log-detail-content {
    margin-top: 6px;
    padding: 8px 10px;
    border-radius: 6px;
    background: #f1f5f9;
    color: #475569;
    font-size: 12px;
    line-height: 1.5;
    white-space: pre-wrap;
  }

  .log-detail-skeleton {
    display: block;
    margin-top: 6px;
    padding: 10px;
    border-radius: 6px;
    background: #f1f5f9;
  }

  .actor-tag {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 4px;
    font-weight: 500;
  }

  .actor-system {
    background: #f1f5f9;
    color: #64748b;
  }

  .actor-user {
    background: #eff6ff;
    color: #1d4ed8;
    cursor: pointer;

    &:hover {
      background: #dbeafe;
    }

    .copy-actor-btn {
      font-size: 10px;
      opacity: 0.7;
    }
  }
}

.log-step-divider {
  ::v-deep &.ant-divider-horizontal {
    margin: 0 0 5px 0;
    border-top-color: #f0f0f0;
  }
}

.skeleton-log-item {
  padding-left: 24px;
}

.error-container {
  .error-icon {
    font-size: 36px;
    color: #ef4444;
    margin-bottom: 8px;
  }
  .error-title {
    font-weight: 700;
    color: #1e293b;
    font-size: 14px;
  }
  .error-desc {
    font-size: 12px;
    max-width: 280px;
    margin: 0 auto 12px;
  }
}

.drawer-footer {
  background: #f8fafc;
  padding: 10px 20px;
  border-top: 1px solid #eef2f6;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .footer-timezone-info {
    font-size: 11px;
    color: #64748b;
    display: flex;
    align-items: center;
  }
}

.fs-10px {
  font-size: 10px;
}

.fs-12px {
  font-size: 12px;
}
</style>
